'use client';

import React from 'react';
import Link from 'next/link';
import { ChevronRight, X } from 'lucide-react';
import { FREE_LESSONS } from './lessonData';

interface FreeLessonNavProps {
  activeId: string;
  onLessonClick: (id: string) => void;
  isOpen: boolean;
  onClose: () => void;
}

export default function FreeLessonNav({
  activeId,
  onLessonClick,
  isOpen,
  onClose,
}: FreeLessonNavProps) {
  return (
    <aside
      className={`
        fixed lg:sticky top-0 lg:top-16 left-0 bottom-0 z-40
        w-72 xl:w-80 flex-shrink-0
        flex flex-col
        transition-transform duration-300 lg:translate-x-0
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:h-[calc(100vh-64px)] overflow-y-auto
      `}
      style={{ backgroundColor: '#F9F7F4', borderRight: '1px solid #E5E0D8' }}
      aria-label="Free lesson navigation"
    >
      {/* Header */}
      <div
        className="px-5 py-4 border-b flex items-center justify-between flex-shrink-0"
        style={{ borderColor: '#E5E0D8' }}
      >
        <div>
          <p
            className="font-body text-xs uppercase tracking-widest font-semibold mb-0.5"
            style={{ color: '#D4891A', letterSpacing: '0.12em' }}
          >
            Free Lessons
          </p>
          <h2 className="font-display font-semibold text-base" style={{ color: '#1B2A4A' }}>
            Three free lessons — no account needed.
          </h2>
        </div>
        <button
          onClick={onClose}
          className="lg:hidden p-1.5 rounded-lg transition-colors duration-150"
          style={{ color: '#9CA3AF' }}
          aria-label="Close navigation"
        >
          <X size={18} />
        </button>
      </div>

      {/* Lesson list */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto" aria-label="Free lesson list">
        <div className="flex flex-col gap-0.5">
          {FREE_LESSONS.map((lesson) => {
            const isActive = activeId === lesson.id;
            return (
              <button
                key={lesson.id}
                onClick={() => onLessonClick(lesson.id)}
                className="w-full text-left px-3 py-2.5 rounded-lg flex items-center gap-3 transition-all duration-150 group"
                style={{ backgroundColor: isActive ? '#1B2A4A' : 'transparent' }}
                aria-current={isActive ? 'page' : undefined}
              >
                <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
                  {isActive ? (
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#D4891A' }} aria-hidden="true" />
                  ) : (
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#C0C0C0' }} aria-hidden="true" />
                  )}
                </span>
                <span className="flex-1 min-w-0">
                  <span
                    className="block font-body text-xs mb-0.5"
                    style={{ color: isActive ? 'rgba(255,255,255,0.5)' : '#9CA3AF' }}
                  >
                    Free Lesson {lesson.number}
                  </span>
                  <span
                    className="block font-body text-sm font-medium leading-tight truncate"
                    style={{ color: isActive ? 'white' : '#1B2A4A' }}
                  >
                    {lesson.title}
                  </span>
                </span>
                {!isActive && (
                  <ChevronRight
                    size={14}
                    className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                    style={{ color: '#9CA3AF' }}
                    aria-hidden="true"
                  />
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Footer CTA */}
      <div className="p-4 border-t flex-shrink-0" style={{ borderColor: '#E5E0D8' }}>
        <Link
          href="/conversion-page"
          className="block w-full text-center font-body text-sm font-semibold py-3 px-4 rounded-xl transition-all duration-200 active:scale-95"
          style={{ backgroundColor: '#D4891A', color: 'white' }}
        >
          Get Full Access — $67
        </Link>
        <p className="text-center mt-2 font-body text-xs" style={{ color: '#9CA3AF' }}>
          Pay once. Lifetime access.
        </p>
      </div>
    </aside>
  );
}
